import styled from '@emotion/native';
import type { LocationAcuityCode } from '@quromedical/fhir-common';
import type { UserPreferenceWard } from '@quromedical/models';
import { Col, Row } from 'components/base';
import { Button, Dot, type IconName, Visible } from 'design-system';
import { COLOR_DARK, MARGIN } from 'design-system/theme';
import React, { useCallback } from 'react';
import { FlatList, type ListRenderItem, View } from 'react-native';

import {
  DropdownButtonWrapper,
  DropdownContainer,
  DropdownListContainer,
  getAcuityColor,
} from './internals';

type ExtendedUserPreferenceWard = UserPreferenceWard & {
  acuity?: LocationAcuityCode;
};

const Wrapper = styled(Col)(() => ({
  flex: 1,
  marginHorizontal: MARGIN.s,
}));

type OnSelect = (id: string) => void;
type WardComponentProps = ExtendedUserPreferenceWard & {
  onSelect: OnSelect;
};

export const WardComponent: React.FC<WardComponentProps> = ({
  name,
  onSelect,
  id,
  acuity,
}) => {
  const color = getAcuityColor(acuity);

  return (
    <Row unsetZIndex flex={1} flexDirection="row" alignItems="center">
      <Dot color={color} />
      <Button
        textPosition="flex-start"
        size="full-width"
        variant="text"
        onPress={() => onSelect(id)}
        text={name}
      />
    </Row>
  );
};

export const WardSelect: React.FC<{
  isSmall: boolean;
  wards: ExtendedUserPreferenceWard[];
  selected?: string;
  onSelect: OnSelect;
}> = ({ isSmall, onSelect, selected, wards }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const toggle = useCallback(() => setIsOpen((prev) => !prev), []);
  const icon: IconName = isOpen ? 'expand-less' : 'expand-more';

  const onItemSelect = useCallback(
    (id: string) => {
      onSelect(id);
      toggle();
    },
    [toggle, onSelect],
  );

  const wardAcuity = wards.find(({ id }) => id === selected)?.acuity;
  const wardName =
    wards.find(({ id }) => id === selected)?.name || 'Select Ward';
  const renderItem: ListRenderItem<UserPreferenceWard> = useCallback(
    ({ item }) => {
      return <WardComponent {...item} onSelect={onItemSelect} />;
    },
    [onItemSelect],
  );

  if (!wards.length) {
    return null;
  }

  const color = getAcuityColor(wardAcuity);

  return (
    <Wrapper unsetZIndex>
      <DropdownContainer isSmall={isSmall}>
        <DropdownButtonWrapper>
          <View
            style={{
              backgroundColor: COLOR_DARK.divider,
              width: '100%',
              borderRadius: 4,
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              padding: isSmall ? 0 : 4,
              marginHorizontal: 4,
              gap: 0,
            }}
          >
            <Dot color={color} />
            <Button
              iconPosition="right"
              icon={icon}
              text={wardName}
              variant="text"
              onPress={toggle}
            />
          </View>
        </DropdownButtonWrapper>

        <Visible if={isOpen}>
          <DropdownListContainer>
            <FlatList
              renderItem={renderItem}
              data={wards}
              keyExtractor={(i) => i.id}
            />
          </DropdownListContainer>
        </Visible>
      </DropdownContainer>
    </Wrapper>
  );
};
